import { useState } from "react";
import { Button, Input } from "@shop-ledger/ui-components";

/**
 * Free-form key/value editor for `InventoryItem.custom_fields` (business-
 * owner request: "custom fields in cases where existing fields don't cover
 * everything exhaustively"). Deliberately a flat string->string bag, no
 * tenant-wide field-definition schema — see
 * `backend/app/models/inventory.py`'s `custom_fields` comment for the scope
 * decision. Used on both the "New item" form (InventoryListPage) and the
 * edit form (InventoryDetailPage).
 */
export function CustomFieldsEditor({
  value,
  onChange,
  disabled,
}: {
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
}) {
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const entries = Object.entries(value);

  function updateEntry(oldKey: string, key: string, val: string) {
    const next = { ...value };
    delete next[oldKey];
    if (key) next[key] = val;
    onChange(next);
  }

  function removeEntry(key: string) {
    const next = { ...value };
    delete next[key];
    onChange(next);
  }

  function addEntry() {
    const key = newKey.trim();
    if (!key) return;
    onChange({ ...value, [key]: newValue });
    setNewKey("");
    setNewValue("");
  }

  return (
    <div className="sl-custom-fields">
      <span className="sl-field__label">Custom fields</span>
      {entries.length === 0 ? <p className="muted">No custom fields yet.</p> : null}
      {entries.map(([key, val]) => (
        <div key={key} className="sl-custom-fields__row">
          <Input
            aria-label="Field name"
            value={key}
            disabled={disabled}
            onChange={(e) => updateEntry(key, e.target.value, val)}
            placeholder="Field name"
          />
          <Input
            aria-label="Field value"
            value={val}
            disabled={disabled}
            onChange={(e) => updateEntry(key, key, e.target.value)}
            placeholder="Value"
          />
          {!disabled ? (
            <Button type="button" variant="ghost" size="md" onClick={() => removeEntry(key)}>
              Remove
            </Button>
          ) : null}
        </div>
      ))}
      {!disabled ? (
        <div className="sl-custom-fields__row">
          <Input aria-label="New field name" value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="e.g. Color" />
          <Input aria-label="New field value" value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="e.g. Red" />
          <Button type="button" variant="secondary" size="md" onClick={addEntry} disabled={!newKey.trim()}>
            Add field
          </Button>
        </div>
      ) : null}
    </div>
  );
}
